import { useEffect, useState, type ReactNode } from 'react';
import { PublicClientApplication, EventType, type EventMessage, type AuthenticationResult } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { buildMsalConfig } from './msal-config.js';
import { WaitNote } from '../components/wait-note.js';

/**
 * Wraps the app in MSAL, and does not render it until MSAL is ready.
 *
 * initialize() is asynchronous in MSAL Browser v3 and must finish before any
 * other call. Rendering children first produces an
 * "uninitialized_public_client_application" error from whichever component
 * happens to ask for a token first, which points at the wrong place entirely.
 *
 * handleRedirectPromise() must then run to completion, because a redirect
 * returns to the app with the response in the URL fragment. Skipping it leaves
 * the person apparently signed out immediately after signing in.
 */

/**
 * Created once, lazily.
 *
 * Not inside the effect: React StrictMode runs effects twice in development, so
 * an instance built there is built twice. The first consumes the redirect
 * response and writes the account to the cache; the second starts fresh and
 * races it, and the app comes back from a successful sign-in still showing
 * "Sign in".
 *
 * Not at module scope either: buildMsalConfig() throws when an environment
 * variable is missing, and a module-scope throw escapes every error boundary and
 * renders a white page. Lazy gets both - one instance, and a failure the caller
 * can catch and explain.
 */
let cachedInstance: PublicClientApplication | null = null;

function getMsalInstance(): PublicClientApplication {
  cachedInstance ??= new PublicClientApplication(buildMsalConfig());
  return cachedInstance;
}

type Status = 'starting' | 'ready' | 'failed';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('starting');
  const [error, setError] = useState<string | null>(null);
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      try {
        // Inside the try so a configuration failure is reported rather than
        // thrown into nothing. getMsalInstance() returns the same instance on
        // StrictMode's second run.
        const msalInstance = getMsalInstance();
        await msalInstance.initialize();

        const response = await msalInstance.handleRedirectPromise();
        if (response?.account) {
          msalInstance.setActiveAccount(response.account);
        } else if (!msalInstance.getActiveAccount()) {
          // A returning visitor in the same tab already has an account cached.
          const [first] = msalInstance.getAllAccounts();
          if (first) msalInstance.setActiveAccount(first);
        }

        // Keep the active account in step with later sign-ins.
        msalInstance.addEventCallback((event: EventMessage) => {
          if (
            (event.eventType === EventType.LOGIN_SUCCESS ||
              event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
            event.payload
          ) {
            const payload = event.payload as AuthenticationResult;
            if (payload.account) msalInstance.setActiveAccount(payload.account);
          }
        });

        if (!cancelled) {
          setMsalInstance(msalInstance);
          setStatus('ready');
        }
      } catch (cause) {
        if (cancelled) return;
        setError(cause instanceof Error ? cause.message : String(cause));
        setStatus('failed');
      }
    }

    void start();
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === 'starting') {
    return (
      <div className="app-frame">
        <main className="app-main">
          <WaitNote className="empty">Starting…</WaitNote>
        </main>
      </div>
    );
  }

  if (status === 'failed') {
    // Deliberately not a blank page. A misconfigured authority or an unregistered
    // redirect URI fails here, and the message is the only thing that says which.
    return (
      <div className="app-frame">
        <main className="app-main">
          <section className="panel">
            <h2>Sign-in is not configured</h2>
            <p className="empty">{error}</p>
          </section>
        </main>
      </div>
    );
  }

  if (!msalInstance) return null;

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}
